import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Chip,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getManifest, resolveUrl } from '../../services/galleryService';

export const ImageGalleryPicker = ({ open, onClose, onSelect, currentImageUrl, title = 'Escolher Imagem' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [activeTag, setActiveTag] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    getManifest()
      .then((data) => {
        setManifest(data);
        // Pre-select current image if any
        if (currentImageUrl) {
          const found = data.images.find((img) => resolveUrl(img.fullUrl) === currentImageUrl);
          if (found) setSelectedId(found.id);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [open, currentImageUrl]);

  const filteredImages = useMemo(() => {
    if (!manifest) return [];
    if (!activeTag) return manifest.images;
    return manifest.images.filter((img) => img.tags.includes(activeTag));
  }, [manifest, activeTag]);

  const selectedImage = useMemo(
    () => manifest?.images.find((img) => img.id === selectedId) || null,
    [manifest, selectedId],
  );

  const handleSelect = () => {
    if (selectedImage) {
      onSelect(resolveUrl(selectedImage.fullUrl));
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Category filters */}
        {manifest && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            <Chip
              label="Todas"
              variant={activeTag === null ? 'filled' : 'outlined'}
              color={activeTag === null ? 'primary' : 'default'}
              onClick={() => setActiveTag(null)}
              size="small"
            />
            {manifest.categories.map((cat) => (
              <Chip
                key={cat.id}
                label={cat.label}
                variant={activeTag === cat.id ? 'filled' : 'outlined'}
                color={activeTag === cat.id ? 'primary' : 'default'}
                onClick={() => setActiveTag(cat.id)}
                size="small"
              />
            ))}
          </Box>
        )}

        {/* Image grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 1,
          }}
        >
          {loading
            ? Array.from({ length: 12 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" sx={{ aspectRatio: '4/3', borderRadius: 1 }} />
              ))
            : filteredImages.map((img) => {
                const isSelected = img.id === selectedId;
                return (
                  <Box
                    key={img.id}
                    onClick={() => setSelectedId(img.id)}
                    sx={{
                      position: 'relative',
                      aspectRatio: '4/3',
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: isSelected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                      '&:hover': { opacity: 0.85 },
                    }}
                  >
                    <img
                      src={resolveUrl(img.thumbUrl)}
                      alt={img.alt}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    {isSelected && (
                      <CheckCircleIcon
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          color: 'primary.main',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          fontSize: 24,
                        }}
                      />
                    )}
                  </Box>
                );
              })}
        </Box>

        {/* Credit */}
        {selectedImage && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Foto: {selectedImage.credit}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSelect} disabled={!selectedId}>
          Selecionar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
